import { zodResolver } from "@hookform/resolvers/zod";
import { type ChangeEvent, useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";

import { Button } from "@/app/components/ui/button";
import { Checkbox } from "@/app/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/app/components/ui/dialog";
import { Field, FieldError, FieldLabel } from "@/app/components/ui/field";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { NativeSelect } from "@/app/components/ui/native-select";
import { setAuthorizationToken } from "@/app/services/auth/authToken";
import type { ResumeContactInfo as ContactInfo, ResumeContextValue, SelectedTemplate } from "@/app/types/resume";
import tips from "../../../assets/copy/tips";
import AutoSuggestion from "../../../components/AutoSuggestion";
import ErrorToast from "../../../components/ErrorToast/";
import ResumePreview from "../../../components/ResumeBuilder/ResumePreview/ResumePreview";
import SectionActions from "../../../components/ResumeBuilder/SectionActions";
import Spinner from "../../../components/Spinner";
import Tips from "../../../components/Tips";
import ResumeContext from "../../../context/ResumeContext";
import { NavigationHandler } from "../../../handlers/navigationHandler";
import { isValidEmail, isValidLocationInHeaderView, isValidPhoneNumber } from "./../../../handlers/validationFunctions";
import { PreviousButton } from "../../../handlers/valueTransformation";
import { isEducationFirst, nextAfterPersonalInfo } from "../../../router/sectionOrder";
import { StaticValue } from "../../../seed/constants";
import { getJobFunctions } from "../../../seed/getMetadata";
import mapping from "../../../seed/metadata/filemapping";
import { useCreateResume, usePatchResumeSection } from "../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../services/create-resume/resumeSections";

/**
 * Validation for the field layer only. Message strings are copied verbatim from
 * the legacy page. Every rule is scoped to the field it belongs to: a format
 * check fires as soon as that one field holds a value, so a blank field
 * elsewhere on the form no longer suppresses a malformed email on the field the
 * user is actually typing in.
 */
const contactInfoSchema = z
	.object({
		firstName: z.string(),
		middleName: z.string(),
		lastName: z.string(),
		email: z.string(),
		phoneNo: z.string(),
		country: z.string(),
		address: z.string(),
		resumeTitle: z.string(),
		resumeFunction: z.string(),
		legalNameToSAcknowledged: z.boolean(),
	})
	.superRefine((values, context) => {
		const requiredFields = [
			["firstName", values.firstName, "First name is required"],
			["lastName", values.lastName, "Last name is required"],
			["email", values.email, "Email is required"],
			["phoneNo", values.phoneNo, "Phone number is required"],
			["address", values.address, "Address is required"],
			["country", values.country, "Country is required"],
			["resumeTitle", values.resumeTitle, "Resume title is required"],
			["resumeFunction", values.resumeFunction, "Area of Expertise is required"],
		] as const;

		for (const [field, value, message] of requiredFields) {
			if (value === "") {
				context.addIssue({
					code: z.ZodIssueCode.custom,
					message,
					path: [field],
				});
			}
		}

		// Format rules skip an empty value so the field reads "required" rather
		// than stacking two messages on a field the user has not filled in yet.
		if (values.address !== "" && !isValidLocationInHeaderView(values.address)) {
			context.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Please enter a valid address eg District , Region ",
				path: ["address"],
			});
		}

		if (values.email !== "" && !isValidEmail(values.email)) {
			context.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Please enter a valid email address",
				path: ["email"],
			});
		}

		if (values.phoneNo !== "" && !isValidPhoneNumber(values.phoneNo)) {
			context.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Please enter a valid phone number eg:  255712XXXXXX",
				path: ["phoneNo"],
			});
		}

		if (!values.legalNameToSAcknowledged) {
			context.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Must Confirm Names-Terms-of-Service to proceed",
				path: ["legalNameToSAcknowledged"],
			});
		}
	});

type ContactInfoFormValues = z.infer<typeof contactInfoSchema>;

/**
 * The preview's column, and the room the action bar has to leave for it — the
 * same width plus the row's gap.
 *
 * The column is stated rather than left to the preview to size itself, because
 * `SectionActions` is fixed and cannot measure a column it is not inside. Sized
 * by its content, the bar ran the full width of the section — past the form its
 * buttons belong to, and under the preview beside it.
 *
 * A share rather than a width, so the preview still gives way on a narrow window
 * the way it did as a flex item. A third of the section is what it settled at,
 * and a third of the section's own cap is the 380px the preview is drawn at.
 */
const PREVIEW_COLUMN = "md:grid-cols-[minmax(0,1fr)_33%]";
const PREVIEW_COLUMN_CLEARANCE = "md:mr-[calc(33%_+_3rem)]";

interface TemplateSelectionState {
	selectedTemplate: SelectedTemplate;
	navigationFlow: unknown;
}

const ResumeContactInfo = () => {
	const template = useSelector(
		(state: { TemplateSelectionReducer: TemplateSelectionState }) => state.TemplateSelectionReducer,
	);

	const selectedTemplate = template.selectedTemplate;
	const navigationHandler = new NavigationHandler();

	const locations: string[] = mapping["districtsRegions"][0].districtRegions;

	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;
	const { education, workHistory, journal } = resumeData;
	const resumeCurrentStatus = journal ? journal.currentStatus : false;
	const isComplete = resumeCurrentStatus == "COMPLETED";

	const educationFirst = isEducationFirst(selectedTemplate?.yearsOfExperience);

	// Education or Work History depending on the level, landing on whichever screen
	// that section owns — the summary if it already has entries to show.
	const nextSection = () =>
		nextAfterPersonalInfo(educationFirst, {
			educationCount: education.length,
			workHistoryCount: workHistory.length,
		});

	useEffect(() => {
		navigationHandler.template = template;
	}, [template]);

	const createResume = useCreateResume();
	const patchSection = usePatchResumeSection();
	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const nextTxt = "Continue";
	const PreviousTxt = "Exit";

	// Watch the cookie names this screen actually reads. `useCookies` only
	// re-renders for the names it is given, and `"resume"` names a cookie that does
	// not exist, so `cookies` stayed frozen at whatever it held on mount while
	// `resume_id` changed underneath it. That pointer does change while this screen
	// is open: the API interceptor clears it on a 401 for an anonymous author, and a
	// reader whose 7-day cookie lapsed has it re-adopted from the journal list.
	// Frozen either way, the submit below picks the wrong branch — patching a pointer
	// that was just cleared, or creating a second resume for a reader who already has
	// one, which the server refuses with a 409.
	const [cookies, setCookie] = useCookies<string>([staticValues.localStorageResumeId, "Authorization"]);

	const navigate = useNavigate();

	const { contactInfo } = resumeData;

	const {
		firstName,
		middleName,
		lastName,
		phoneNo,
		email,
		country,
		resumeTitle,
		resumeFunction,
		legalNameToSAcknowledged,
	} = contactInfo;

	// The combined "city, region" line. Autofilled from the context (seeded or
	// fetched) whenever both parts are present — the "seed comparison" autofill.
	const [address, setAddress] = useState("");
	useEffect(() => {
		if (!(contactInfo.city === "") && !(contactInfo.region === "")) {
			setAddress(`${contactInfo.city}, ${contactInfo.region}`);
		}
	}, [contactInfo]);

	// Field layer: values come from the context so a server fetch fills the form.
	//
	// `mode: "onTouched"` holds every message back until the field is left for the
	// first time, so a half-typed email is never called wrong. After that first
	// blur the field re-checks on each keystroke, which is what clears the message
	// the moment the address is fixed instead of making the user tab out again.
	//
	// Both `resetOptions` exist for the same reason: every keystroke runs
	// `syncContext`, which rewrites the context and hands `values` a new object,
	// and react-hook-form resets the form whenever `values` changes. A plain reset
	// drops `formState.errors` (the message would vanish on the next character)
	// and `touchedFields` — and losing touched state would strand the message,
	// since the rule above consults it to decide whether to re-check at all.
	//
	// `isValid` runs the whole schema rather than just the fields touched so far,
	// which is what Continue needs in order to gate on. Reading it here is also
	// what switches it on — react-hook-form only recomputes the flag once
	// something subscribes to it. `keepIsValid` is deliberately absent from
	// `resetOptions`: each of those per-keystroke resets re-runs the check, which
	// is what keeps the button in step with the form.
	const {
		formState: { errors, isValid },
		handleSubmit: submitForm,
		register,
		setValue,
	} = useForm<ContactInfoFormValues>({
		resolver: zodResolver(contactInfoSchema),
		mode: "onTouched",
		resetOptions: { keepErrors: true, keepTouched: true },
		values: {
			firstName,
			middleName,
			lastName,
			email,
			phoneNo,
			country,
			address,
			resumeTitle,
			resumeFunction,
			legalNameToSAcknowledged,
		},
	});

	// Mirror of the legacy onChangeInput: every keystroke writes the field plus
	// the current address split into the context, so the side preview stays live.
	const syncContext = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;

		setResumeData((prevState) => {
			const [city, region] = address.split(",").map((str) => str.trim());

			return {
				...prevState,
				contactInfo: {
					...prevState.contactInfo,
					[name]: value,
					address,
					city,
					region,
				},
			};
		});
	};

	//form submission handler
	function successCreateNewResume(ledger: { _id: string; journal: { user: { _id: string } } }) {
		const expireDate = new Date();
		if (!cookies[staticValues.localStorageResumeId]) {
			expireDate.setDate(expireDate.getDate() + 30);
			setCookie(staticValues.localStorageResumeId, ledger._id, {
				path: "/",
				expires: expireDate,
			});
		}
		const authHeader = ledger.journal.user._id;
		if (!cookies["Authorization"]) {
			//it looks like the data.data.journal.user._id does not come during patching, will confirm. This is to prevent setting undefine if we already have a cookie
			expireDate.setDate(expireDate.getDate() + 30);
			setAuthorizationToken(authHeader, expireDate);
		}

		const skipOne = education.length > 0 ? 1 : 0;
		PreviousButton(true, PreviousTxt, navigationHandler, skipOne);

		return navigate(`/resume/${nextSection()}`); //temporary link
	}

	function successPatchingContactInfo() {
		if (isComplete) return navigate("/my-resumes");

		return navigate(`/resume/${nextSection()}`);
	}

	function nextButton(payloadContactInfo: ContactInfo) {
		setServerErrors([]);
		const inputData = {
			data: {
				contactInfo: payloadContactInfo,
				templateId: selectedTemplate?.templateName ? selectedTemplate.templateName : "fresher_template",
				yearsOfExperience: selectedTemplate?.yearsOfExperience,
				resumeTitle: resumeTitle + "-" + resumeFunction,
			},
		};

		if (!cookies[staticValues.localStorageResumeId]) {
			//user not registered or tried to create a new resume. We will check if we can create new or already has resume
			createResume.mutate(inputData as never, {
				onSuccess: successCreateNewResume,
				onError: errorCreateNewResume,
			});
		} else {
			//user has account or already in resume progress
			const resumeId = cookies[staticValues.localStorageResumeId];
			patchSection.mutate(
				{
					resumeId,
					section: RESUME_SECTIONS.HEADING,
					data: inputData,
				} as never,
				{
					onSuccess: successPatchingContactInfo,
					onError: errorCreateNewResume,
				},
			);
		}
	}

	const [showLoginPopup, setShowLoginPopup] = useState(false);
	const [showBlockedPopup, setShowBlockedPopup] = useState(false);
	const [blockedPopupData, setBlockedPopupData] = useState("");

	const [serverErrors, setServerErrors] = useState<string[]>([]);

	function errorCreateNewResume(error: Error) {
		const data = error as unknown as {
			response: { status: number; data: { message: string | string[] } };
		};
		const serverResponse = data.response.data;

		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...(serverResponse.message as string[])];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message as string];
				});

		if (data.response) {
			const response = data.response.status;

			if (response === 409) {
				//resume exist. If account exists redirect them to login
				setShowLoginPopup(true);
			}
			if (response === 412) {
				setShowBlockedPopup(true);
				setServerErrors([]);
				setBlockedPopupData(data.response.data.message as string);
			}
		}
	}

	// Orchestration on a valid field layer: split the address, write the full
	// contact object to the context, then create or patch.
	const onValidSubmit = () => {
		const [city, region] = address.split(",").map((str) => str.trim());

		const payloadContactInfo: ContactInfo = {
			...contactInfo,
			city: city,
			region: region,
		};

		setResumeData((prevState) => {
			return {
				...prevState,
				contactInfo: {
					firstName,
					middleName,
					lastName,
					phoneNo,
					address,
					email,
					city,
					country,
					region,
					legalNameToSAcknowledged,
					resumeFunction,
					resumeTitle,
				},
			};
		});
		return nextButton(payloadContactInfo);
	};

	// An invalid submit no longer raises a form-level message: every failing field
	// renders its own text inline, and react-hook-form focuses the first one.
	const addContactInfo = () => {
		setServerErrors([]);
		submitForm(onValidSubmit)();
	};

	const isSaving = createResume.isPending || patchSection.isPending;

	const blockedPopupLink =
		blockedPopupData.includes(":") && blockedPopupData.substring(blockedPopupData.indexOf(":") + 1).split(":", 2)[1];

	return (
		<>
			<Dialog open={showLoginPopup} onOpenChange={setShowLoginPopup}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>In-progress Resume Exists, Login to Continue</DialogTitle>
						<DialogDescription>
							You already have an existing resume using this email address, to continue please log in or use a different
							email
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button type="button" variant="secondary" onClick={() => setShowLoginPopup(false)}>
							Stay
						</Button>
						<Link to="/signin">
							<Button type="button">Log In</Button>
						</Link>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={showBlockedPopup} onOpenChange={setShowBlockedPopup}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Closed Alpha</DialogTitle>
					</DialogHeader>
					<span
						dangerouslySetInnerHTML={{
							__html: blockedPopupData.includes(":") ? blockedPopupData.split(":")[0] : blockedPopupData,
						}}
					/>
					<DialogFooter>
						<a href={blockedPopupLink || undefined} target="_blank" rel="noreferrer">
							<Button type="button">Secure a Slot</Button>
						</a>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<div>
				<div className="mb-3 text-gray-600">
					<p>Enter your contact details to ensure recruiters can easily reach you.</p>
				</div>

				<div className={`grid gap-12 ${PREVIEW_COLUMN}`}>
					<form noValidate onSubmit={(e) => e.preventDefault()}>
						{/* Server-side failures only. Field-level problems are reported inline
                on the field that caused them, never here. */}
						{serverErrors.map((item, index) => {
							return <ErrorToast error={item} key={index} />;
						})}
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<Field className="" data-invalid={!!errors.firstName}>
								<FieldLabel htmlFor="contact-first-name">First Name*</FieldLabel>
								<Input
									id="contact-first-name"
									type="text"
									placeholder="First Name"
									autoComplete="off"
									aria-invalid={!!errors.firstName}
									{...register("firstName", { onChange: syncContext })}
								/>
								<FieldError className="text-error-default" errors={[errors.firstName]} />
							</Field>

							<Field className="" data-invalid={!!errors.lastName}>
								<div className="flex items-center justify-between">
									<FieldLabel htmlFor="contact-last-name">Last Name*</FieldLabel>
									<Tips content={tips.contactInformation.contact.name} />
								</div>
								<Input
									id="contact-last-name"
									type="text"
									placeholder="Last Name"
									autoComplete="off"
									aria-invalid={!!errors.lastName}
									{...register("lastName", { onChange: syncContext })}
								/>
								<FieldError className="text-error-default" errors={[errors.lastName]} />
							</Field>

							<Field className=" md:col-span-2" data-invalid={!!errors.middleName}>
								<FieldLabel htmlFor="contact-middle-name">Middle Name</FieldLabel>
								<Input
									id="contact-middle-name"
									type="text"
									placeholder="Middle Name"
									autoComplete="off"
									aria-invalid={!!errors.middleName}
									{...register("middleName", { onChange: syncContext })}
								/>
								<FieldError className="text-error-default" errors={[errors.middleName]} />
							</Field>

							<Field className=" md:col-span-2" data-invalid={!!errors.resumeFunction}>
								<FieldLabel htmlFor="contact-resume-function">Area of Expertise*</FieldLabel>
								<NativeSelect
									id="contact-resume-function"
									aria-invalid={!!errors.resumeFunction}
									{...register("resumeFunction", { onChange: syncContext })}
								>
									<option value="">Select Area of Expertise</option>
									{[...getJobFunctions()].map((item: string, index: number) => {
										return (
											<option key={index} value={item}>
												{item}
											</option>
										);
									})}
								</NativeSelect>
								<FieldError className="text-error-default" errors={[errors.resumeFunction]} />
							</Field>

							<Field className=" md:col-span-2" data-invalid={!!errors.resumeTitle}>
								<FieldLabel htmlFor="contact-resume-title">Professional Title*</FieldLabel>
								<Input
									id="contact-resume-title"
									type="text"
									placeholder="eg: English Teacher, Bank Teller"
									autoComplete="off"
									aria-invalid={!!errors.resumeTitle}
									{...register("resumeTitle", { onChange: syncContext })}
								/>
								<FieldError className="text-error-default" errors={[errors.resumeTitle]} />
							</Field>

							<Field className=" md:col-span-2" data-invalid={!!errors.email}>
								<div className="flex items-center justify-between">
									<FieldLabel htmlFor="contact-email">Email*</FieldLabel>
									<Tips content={tips.contactInformation.email} />
								</div>
								<Input
									id="contact-email"
									type="text"
									placeholder="Email"
									autoComplete="off"
									aria-invalid={!!errors.email}
									onCopy={(e) => e.preventDefault()}
									onPaste={(e) => e.preventDefault()}
									{...register("email", { onChange: syncContext })}
								/>
								<FieldError className="text-error-default" errors={[errors.email]} />
							</Field>

							<Field className=" md:col-span-2" data-invalid={!!errors.phoneNo}>
								<FieldLabel htmlFor="contact-phone-no">Phone Number*</FieldLabel>
								<Input
									id="contact-phone-no"
									type="text"
									placeholder="Phone Number"
									autoComplete="off"
									aria-invalid={!!errors.phoneNo}
									onCopy={(e) => e.preventDefault()}
									onPaste={(e) => e.preventDefault()}
									{...register("phoneNo", { onChange: syncContext })}
								/>
								<FieldError className="text-error-default" errors={[errors.phoneNo]} />
							</Field>

							<Field className="" data-invalid={!!errors.country}>
								<FieldLabel htmlFor="contact-country">Country*</FieldLabel>
								<Input
									id="contact-country"
									type="text"
									placeholder="Country"
									value={country}
									readOnly
									aria-invalid={!!errors.country}
								/>
								<FieldError className="text-error-default" errors={[errors.country]} />
							</Field>

							<div className="mb-4">
								{!(country === "Tanzania") && (
									<Field className="gap-1" data-invalid={!!errors.address}>
										<div className="flex items-center justify-between">
											<FieldLabel htmlFor="contact-address">Ex: City, Region</FieldLabel>
											<Tips content={tips.contactInformation.address} />
										</div>
										<Input
											id="contact-address"
											type="text"
											name="address"
											value={address}
											onChange={syncContext}
											placeholder="Enter Address "
											autoComplete="off"
											aria-invalid={!!errors.address}
										/>
										<FieldError className="text-error-default" errors={[errors.address]} />
									</Field>
								)}
								{country === "Tanzania" && (
									<AutoSuggestion
										suggestions={locations}
										label="Eg: District, Region"
										type="text"
										name="address"
										userInput={address}
										setUserInput={(value) => {
											setAddress(value);
											// Not a registered input, and `keepErrors` holds the last message,
											// so picking a district has to clear it explicitly.
											setValue("address", value, { shouldValidate: true });
										}}
										isForm={true}
										error={errors.address ? errors.address.message : ""}
										tips={tips.contactInformation.address}
									/>
								)}
							</div>

							<Field className="mb-3 gap-1 md:col-span-2" data-invalid={!!errors.legalNameToSAcknowledged}>
								{/* A plain div, not a label: `Label` renders its own <label>, and nesting
								    one inside another is invalid markup that made the click target
								    inconsistent. `htmlFor` keeps the caption clickable. */}
								<div className="flex items-start gap-3">
									<Checkbox
										id="legalNameToSAcknowledged"
										name="legalNameToSAcknowledged"
										checked={legalNameToSAcknowledged}
										aria-invalid={!!errors.legalNameToSAcknowledged}
										onCheckedChange={(checked) => {
											const isAcknowledged = checked === true;
											// The checkbox is not registered, so the resolver has to be told
											// directly — otherwise the error stays up after it is ticked.
											setValue("legalNameToSAcknowledged", isAcknowledged, {
												shouldValidate: true,
											});
											setResumeData((prev) => {
												return {
													...prev,
													contactInfo: {
														...prev.contactInfo,
														legalNameToSAcknowledged: isAcknowledged,
													},
												};
											});
										}}
									/>
									<Label htmlFor="legalNameToSAcknowledged" className="cursor-pointer">
										I confirm that I have entered my personal information correctly as it appears of my educational
										documents.
									</Label>
								</div>
								<FieldError className="text-error-default" errors={[errors.legalNameToSAcknowledged]} />
							</Field>
						</div>
						<SectionActions className={`flex justify-end ${PREVIEW_COLUMN_CLEARANCE}`}>
							{/* Stays inactive until the whole schema passes, and while a save is
                  in flight so the resume cannot be created twice on a double click. */}
							<Button type="button" disabled={!isValid || isSaving} onClick={() => addContactInfo()}>
								{isSaving ? (
									<>
										Loading <Spinner />
									</>
								) : (
									`${nextTxt}`
								)}
							</Button>
						</SectionActions>
					</form>

					{/* Resume Preview */}
					<div className="flex justify-center">
						<ResumePreview resumeData={resumeData} />
					</div>
					{/* Resume Preview */}
				</div>
			</div>
		</>
	);
};

export default ResumeContactInfo;
